import { useEffect, useState } from 'react';
import { addReason, deleteReason, fetchReasons, reorderReasons, updateReason } from '@/api/reasons';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/TextField';
import { Toggle } from '@/components/ui/Toggle';
import { Badge } from '@/components/ui/Badge';
import { GripIcon } from '@/components/icons';
import { ErrorState, LoadingState } from '@/components/feedback/StateViews';
import { useAsyncData } from '@/hooks/useAsyncData';
import { toast } from '@/store/useToastStore';
import { resolveReturnReasons } from '@/constants/defaults';
import { errorMessageOf } from '@/lib/errors';
import { cn } from '@/lib/cn';
import type { ReturnReason } from '@/types';

const GRID = 'grid grid-cols-[24px_minmax(140px,1fr)_96px_112px_112px_88px_40px] items-center gap-x-3';

export function ReasonsSection() {
  const { data, errorMessage, isLoading, reload } = useAsyncData<ReturnReason[] | null>(
    (signal) => fetchReasons(signal),
    [],
  );
  const [ordered, setOrdered] = useState<ReturnReason[]>([]);
  const [newLabel, setNewLabel] = useState('');
  const [photoRequired, setPhotoRequired] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [openMenuSlug, setOpenMenuSlug] = useState<string | null>(null);
  const [draggingSlug, setDraggingSlug] = useState<string | null>(null);

  useEffect(() => {
    if (data !== undefined) {
      setOrdered(resolveReturnReasons(data));
    }
  }, [data]);

  async function runMutation(action: () => Promise<unknown>, successMessage: string) {
    try {
      await action();
      toast.success(successMessage);
      reload();
    } catch (error) {
      toast.error(errorMessageOf(error));
    }
  }

  async function updateReasonField(
    slug: string,
    patch: Partial<ReturnReason>,
    successMessage: string,
  ) {
    const snapshot = ordered;
    setOrdered((current) =>
      current.map((reason) => (reason.slug === slug ? { ...reason, ...patch } : reason)),
    );
    try {
      await updateReason(slug, patch);
      toast.success(successMessage);
    } catch (error) {
      setOrdered(snapshot);
      toast.error(errorMessageOf(error));
    }
  }

  async function handleAdd() {
    if (!newLabel.trim()) {
      return;
    }
    setIsBusy(true);
    try {
      await addReason({ label: newLabel.trim(), photoRequired, isActive: true });
      toast.success('Reason added');
      setNewLabel('');
      setPhotoRequired(false);
      reload();
    } catch (error) {
      toast.error(errorMessageOf(error));
    } finally {
      setIsBusy(false);
    }
  }

  function handleDrop(targetSlug: string) {
    if (!draggingSlug || draggingSlug === targetSlug) {
      setDraggingSlug(null);
      return;
    }
    const fromIndex = ordered.findIndex((reason) => reason.slug === draggingSlug);
    const toIndex = ordered.findIndex((reason) => reason.slug === targetSlug);
    if (fromIndex === -1 || toIndex === -1) {
      setDraggingSlug(null);
      return;
    }
    const next = [...ordered];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    setOrdered(next);
    setDraggingSlug(null);
    runMutation(() => reorderReasons(next.map((reason) => reason.slug)), 'Order updated');
  }

  if (isLoading && ordered.length === 0) {
    return <LoadingState message="Loading reasons…" />;
  }
  if (errorMessage) {
    return <ErrorState message={errorMessage} onRetry={reload} />;
  }

  return (
    <div className="flex flex-col gap-4">
      <Card padded={false} className="overflow-hidden">
        <div className="border-b border-line px-5 py-4 sm:px-6">
          <h2 className="text-lg font-semibold text-ink">Return reasons</h2>
          <p className="mt-0.5 text-sm text-ink-muted">Shown in the customer portal · drag to reorder</p>
        </div>

        <div className="overflow-x-auto">
          <div className="min-w-[640px]">
            <div className={cn(GRID, 'border-b border-line bg-surface-sunken px-5 py-2.5 sm:px-6')}>
              <span />
              <span className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">Reason</span>
              <span className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">Photos</span>
              <span className="text-center text-xs font-semibold uppercase tracking-wide text-ink-subtle">
                Auto-approve
              </span>
              <span className="text-center text-xs font-semibold uppercase tracking-wide text-ink-subtle">
                Green return
              </span>
              <span className="text-center text-xs font-semibold uppercase tracking-wide text-ink-subtle">
                Active
              </span>
              <span />
            </div>

            <ul className="divide-y divide-line">
              {ordered.map((reason, index) => (
                <li
                  key={reason.slug}
                  draggable
                  onDragStart={() => setDraggingSlug(reason.slug)}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={() => handleDrop(reason.slug)}
                  className={cn(
                    GRID,
                    'px-5 py-3.5 transition-colors sm:px-6',
                    draggingSlug === reason.slug ? 'opacity-50' : 'hover:bg-surface-sunken/50',
                  )}
                >
                  <span className="cursor-grab text-ink-subtle active:cursor-grabbing" aria-label="Drag to reorder">
                    <GripIcon />
                  </span>

                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-ink">{reason.label}</div>
                    <div className="font-mono text-xs text-ink-subtle">{reason.slug}</div>
                  </div>

                  <div>
                    <button
                      type="button"
                      onClick={() =>
                        updateReasonField(
                          reason.slug,
                          { photoRequired: !reason.photoRequired },
                          'Reason updated',
                        )
                      }
                    >
                      {reason.photoRequired ? (
                        <Badge tone="pending">Required</Badge>
                      ) : (
                        <span className="text-sm text-ink-subtle">Optional</span>
                      )}
                    </button>
                  </div>

                  <div className="flex justify-center">
                    <Toggle
                      checked={Boolean(reason.autoApprove)}
                      onChange={(checked) =>
                        updateReasonField(reason.slug, { autoApprove: checked }, 'Reason updated')
                      }
                    />
                  </div>

                  <div className="flex justify-center">
                    <Toggle
                      checked={Boolean(reason.greenReturn)}
                      onChange={(checked) =>
                        updateReasonField(reason.slug, { greenReturn: checked }, 'Reason updated')
                      }
                    />
                  </div>

                  <div className="flex justify-center">
                    <Toggle
                      checked={reason.isActive}
                      onChange={(checked) => {
                        if (!checked && ordered.filter((entry) => entry.isActive).length <= 1) {
                          toast.error("You can't disable all reasons");
                          return;
                        }
                        updateReasonField(reason.slug, { isActive: checked }, 'Reason updated');
                      }}
                    />
                  </div>

                  <div className="relative flex justify-end">
                    <button
                      type="button"
                      aria-label="Reason actions"
                      onClick={() =>
                        setOpenMenuSlug((current) => (current === reason.slug ? null : reason.slug))
                      }
                      className="rounded-md px-1.5 py-1 text-ink-subtle transition-colors hover:bg-surface-sunken hover:text-ink"
                    >
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor" aria-hidden="true">
                        <circle cx="4" cy="9" r="1.5" />
                        <circle cx="9" cy="9" r="1.5" />
                        <circle cx="14" cy="9" r="1.5" />
                      </svg>
                    </button>

                    {openMenuSlug === reason.slug ? (
                      <>
                        <button
                          type="button"
                          aria-hidden="true"
                          tabIndex={-1}
                          className="fixed inset-0 z-30 cursor-default"
                          onClick={() => setOpenMenuSlug(null)}
                        />
                        <div
                          className={cn(
                            'absolute right-0 z-40 w-44 rounded-lg border border-line bg-surface py-1 shadow-card',
                            index === ordered.length - 1 && ordered.length > 1
                              ? 'bottom-full mb-1'
                              : 'top-full mt-1',
                          )}
                        >
                          <button
                            type="button"
                            className="flex w-full items-center px-3 py-2 text-sm text-ink hover:bg-surface-sunken"
                            onClick={() => {
                              setOpenMenuSlug(null);
                              updateReasonField(
                                reason.slug,
                                { photoRequired: !reason.photoRequired },
                                'Reason updated',
                              );
                            }}
                          >
                            {reason.photoRequired ? 'Make photo optional' : 'Require photo'}
                          </button>
                          <button
                            type="button"
                            className="flex w-full items-center px-3 py-2 text-sm text-danger hover:bg-danger-soft"
                            onClick={() => {
                              setOpenMenuSlug(null);
                              runMutation(() => deleteReason(reason.slug), 'Reason deleted');
                            }}
                          >
                            Delete reason
                          </button>
                        </div>
                      </>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Card>

      <Card>
        <h3 className="text-sm font-semibold text-ink">Add a custom reason</h3>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <div className="min-w-56 flex-1">
            <TextField
              label="Label"
              placeholder="e.g. Size runs too large"
              value={newLabel}
              onChange={(event) => setNewLabel(event.target.value)}
            />
          </div>
          <div className="pb-2.5">
            <Toggle label="Require photo" checked={photoRequired} onChange={setPhotoRequired} />
          </div>
          <Button onClick={handleAdd} isLoading={isBusy} disabled={!newLabel.trim()}>
            Add reason
          </Button>
        </div>
      </Card>
    </div>
  );
}
